import { createContext, useContext, useState, type ReactNode } from 'react';
import type {
  Customer,
  QuoteRequest,
  Quote,
  Appointment,
  Team,
  Job,
  RequestStatus,
  QuoteStatus,
  JobStatus,
  Activity,
  Payment,
  Expense,
  AppNotification,
} from '@/types';
import { customers as seedCustomers } from '@/mocks/customers';
import { requests as seedRequests } from '@/mocks/requests';
import { quotes as seedQuotes } from '@/mocks/quotes';
import { appointments as seedAppointments } from '@/mocks/appointments';
import { teams as seedTeams } from '@/mocks/teams';
import { jobs as seedJobs } from '@/mocks/jobs';
import { payments as seedPayments } from '@/mocks/payments';
import { expenses as seedExpenses } from '@/mocks/expenses';
import { notifications as seedNotifications } from '@/mocks/notifications';

interface AppState {
  customers: Customer[];
  requests: QuoteRequest[];
  quotes: Quote[];
  appointments: Appointment[];
  teams: Team[];
  jobs: Job[];
  payments: Payment[];
  expenses: Expense[];
  notifications: AppNotification[];
}

interface AppStoreValue extends AppState {
  setRequestStatus: (id: string, status: RequestStatus) => void;
  addRequestActivity: (id: string, activity: Activity) => void;
  setQuoteStatus: (id: string, status: QuoteStatus) => void;
  setJobStatus: (id: string, status: JobStatus) => void;
  addJobNote: (id: string, note: string) => void;
  addJobTimeline: (id: string, activity: Activity) => void;
  addAppointment: (appointment: Appointment) => void;
  addQuote: (quote: Quote) => void;
  addCustomer: (customer: Customer) => void;
  addPayment: (payment: Payment) => void;
  addExpense: (expense: Expense) => void;
  addNotification: (notification: AppNotification) => void;
  markNotificationRead: (id: string) => void;
  markAllNotificationsRead: () => void;
  setTeamStatus: (id: string, status: string) => void;
  assignTeam: (id: string, currentJob: string, location: string) => void;
}

const AppStoreContext = createContext<AppStoreValue | null>(null);

function timestamp(): string {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, '0');
  const mm = String(now.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

export function AppStoreProvider({ children }: { children: ReactNode }) {
  const [customers, setCustomers] = useState<Customer[]>(seedCustomers);
  const [requests, setRequests] = useState<QuoteRequest[]>(seedRequests);
  const [quotes, setQuotes] = useState<Quote[]>(seedQuotes);
  const [appointments, setAppointments] = useState<Appointment[]>(seedAppointments);
  const [teams, setTeams] = useState<Team[]>(seedTeams);
  const [jobs, setJobs] = useState<Job[]>(seedJobs);
  const [payments, setPayments] = useState<Payment[]>(seedPayments);
  const [expenses, setExpenses] = useState<Expense[]>(seedExpenses);
  const [notifications, setNotifications] = useState<AppNotification[]>(seedNotifications);

  const setRequestStatus = (id: string, status: RequestStatus) => {
    setRequests((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status,
              lastContact: 'Şimdi',
              activities: [...r.activities, { time: timestamp(), text: `Durum: ${status}` }],
            }
          : r,
      ),
    );
  };

  const addRequestActivity = (id: string, activity: Activity) => {
    setRequests((prev) =>
      prev.map((r) => (r.id === id ? { ...r, activities: [...r.activities, activity] } : r)),
    );
  };

  const setQuoteStatus = (id: string, status: QuoteStatus) => {
    setQuotes((prev) => prev.map((q) => (q.id === id ? { ...q, status } : q)));
  };

  const setJobStatus = (id: string, status: JobStatus) => {
    setJobs((prev) =>
      prev.map((j) =>
        j.id === id
          ? { ...j, status, timeline: [...j.timeline, { time: timestamp(), text: status }] }
          : j,
      ),
    );
  };

  const addJobNote = (id: string, note: string) => {
    setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, notes: [...j.notes, note] } : j)));
  };

  const addJobTimeline = (id: string, activity: Activity) => {
    setJobs((prev) =>
      prev.map((j) => (j.id === id ? { ...j, timeline: [...j.timeline, activity] } : j)),
    );
  };

  const addAppointment = (appointment: Appointment) => {
    setAppointments((prev) => [...prev, appointment]);
  };

  const addQuote = (quote: Quote) => {
    setQuotes((prev) => [...prev, quote]);
  };

  const addCustomer = (customer: Customer) => {
    setCustomers((prev) => [...prev, customer]);
  };

  const addPayment = (payment: Payment) => {
    setPayments((prev) => [payment, ...prev]);
  };

  const addExpense = (expense: Expense) => {
    setExpenses((prev) => [expense, ...prev]);
  };

  const addNotification = (notification: AppNotification) => {
    setNotifications((prev) => [notification, ...prev]);
  };

  const markNotificationRead = (id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllNotificationsRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const setTeamStatus = (id: string, status: string) => {
    setTeams((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
  };

  const assignTeam = (id: string, currentJob: string, location: string) => {
    setTeams((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, status: 'Aktif İşte', currentJob, location } : t,
      ),
    );
  };

  const value: AppStoreValue = {
    customers,
    requests,
    quotes,
    appointments,
    teams,
    jobs,
    payments,
    expenses,
    notifications,
    setRequestStatus,
    addRequestActivity,
    setQuoteStatus,
    setJobStatus,
    addJobNote,
    addJobTimeline,
    addAppointment,
    addQuote,
    addCustomer,
    addPayment,
    addExpense,
    addNotification,
    markNotificationRead,
    markAllNotificationsRead,
    setTeamStatus,
    assignTeam,
  };

  return <AppStoreContext.Provider value={value}>{children}</AppStoreContext.Provider>;
}

export function useAppStore(): AppStoreValue {
  const ctx = useContext(AppStoreContext);
  if (!ctx) {
    throw new Error('useAppStore must be used within AppStoreProvider');
  }
  return ctx;
}